import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, PLATFORM_ID, Renderer2 } from '@angular/core';
import { APP_CONFIG } from '@klubovna/shared-data';
import { ButtonColor } from '@klubovna/shared-models';
import { SharedModule } from '../../shared.module';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'k-cookies-consent',
  imports: [ ButtonComponent, SharedModule ],
  templateUrl: './cookies-consent.component.html',
  styleUrl: './cookies-consent.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookiesConsentComponent {
  private readonly config = inject(APP_CONFIG);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  enableSEO = this.config.enableSEO;
  prod = this.config.production;
  isVisible = this.isBrowser ? !localStorage.getItem('cookieConsent') : false;
  buttonColors = ButtonColor;

  constructor() {
    if (!this.isBrowser) {
      return;
    }

    if (this.config.production && this.config.enableSEO) {
      const consent = localStorage.getItem('cookieConsent');
      if (consent === 'true') {
        this.enableTrackingScripts();
      } else if (!consent || consent === 'false') {
        this.disableTrackingScripts();
      }
    }
  }

  acceptCookies() {
    if (!this.isBrowser) {
      return;
    }
    localStorage.setItem('cookieConsent', 'true');
    this.isVisible = false;
    this.enableTrackingScripts();
  }

  declineCookies() {
    if (!this.isBrowser) {
      return;
    }
    localStorage.setItem('cookieConsent', 'false');
    this.isVisible = false;
    this.disableTrackingScripts();
  }

  private enableTrackingScripts() {
    if (!this.isBrowser) {
      return;
    }

    const gaScript = this.document.createElement('script');
    gaScript.id = 'gtag-script';
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=G-PWSP8QSRKQ`;
    this.renderer.appendChild(this.document.head, gaScript);

    gaScript.onload = () => {
      // @ts-expect-error window google initializer
      if (window?.initializeGtag) {
        // @ts-expect-error window google initializer
        window?.initializeGtag();
      } else {
        console.error('Gtag initialization function is not defined');
      }
    };

    const gtmScript = this.document.createElement('script');
    gtmScript.id = 'gtm-script';
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=GTM-WF6Q78HH`;
    this.renderer.appendChild(this.document.head, gtmScript);

    const hjScript = this.document.createElement('script');
    hjScript.id = 'hotjar-script';
    hjScript.innerHTML = `
      (function(c,s,q,u,a,r,e){
        c.hj=c.hj||function(){(c.hj.q=c.hj.q||[]).push(arguments)};
        c._hjSettings={hjid:6376989};
        r=s.getElementsByTagName('head')[0];
        e=s.createElement('script');e.async=1;
        e.src=q+c._hjSettings.hjid+u;
        r.appendChild(e);
      })(window,document,'https://static.hj.contentsquare.net/c/csq-','.js',6376989);
    `;
    this.renderer.appendChild(this.document.head, hjScript);
  }

  private disableTrackingScripts() {
    if (!this.isBrowser) {
      return;
    }

    const gtagScript = this.document.getElementById('gtag-script');
    if (gtagScript) {
      gtagScript.remove();
    }

    const gtmScript = this.document.getElementById('gtm-script');
    if (gtmScript) {
      gtmScript.remove();
    }

    const hjScript = this.document.getElementById('hotjar-script');
    if (hjScript) {
      hjScript.remove();
    }
  }
}
